export type OptaUUID = string

export type OptaCompetition = {
  id: OptaUUID
  name: string
}

export type OptaStage = {
  id: OptaUUID
  name: string
}

export type OptaSeries = {
  id: OptaUUID
  name: string
}

export type OptaTournamentCalendar = {
  id?: OptaUUID
  endDate?: string
  startDate?: string
  name?: string
}

export enum TeamPosition {
  Home = 'home',
  Away = 'away',
}

export type OptaTeamPosition = `${TeamPosition}`

export type OptaContestant = {
  id: OptaUUID
  name: string
  shortName?: string
  officialName: string
  code?: string
  position: OptaTeamPosition
}

export type OptaMatchInfo = {
  id: OptaUUID
  date: string
  time?: string
  postMatch?: boolean
  lastUpdated: string
  competition: OptaCompetition
  stage: OptaStage
  series?: OptaSeries
  tournamentCalendar: OptaTournamentCalendar
  contestant?: OptaContestant[]
  week: string
}

export enum PeriodId {
  FirstHalf = '1',
  SecondHalf = '2',
  ExtraTimeFirstHalf = '3',
  ExtraTimeSecondHalf = '4',
  PenaltyShootout = '5',
  HalfTime = '10',
  EndOfSecondHalfBeforeExtraTime = '11',
  ExtraTimeHalfTime = '12',
  EndOfExtraTimeBeforePenalties = '13',
  FullTime = '14',
  PreMatch = '16',
}
export enum MatchStatus {
  Fixture = 'Fixture',
  Playing = 'Playing',
  Played = 'Played',
  Cancelled = 'Cancelled',
  Postponed = 'Postponed',
  Suspended = 'Suspended',
  Awarded = 'Awarded',
}
export enum Winner {
  Home = 'home',
  Draw = 'draw',
  Away = 'away',
}
export enum MatchPeriodId {
  FirstHalf = '1',
  SecondHalf = '2',
  ExtraTimeFirstHalf = '3',
  ExtraTimeSecondHalf = '4',
}
export type OptaPeriodId = `${PeriodId}`
export type OptaMatchStatus = `${MatchStatus}`
export type OptaDynamicValue = number
export type OptaWinner = `${Winner}`
export type OptaMatchPeriodId = `${MatchPeriodId}`

export type OptaMatchPeriod = {
  id: OptaMatchPeriodId
  lengthMin?: number
  lengthSec?: number
  start?: string
  end?: string
}
export type OptaScore = {
  home: number
  away: number
}

export type OptaMatchScores = {
  ht?: OptaScore
  ft?: OptaScore
  et?: OptaScore
  total?: OptaScore
}

export type OptaMatchDetails = {
  periodId: OptaPeriodId
  matchStatus: OptaMatchStatus
  matchTime?: OptaDynamicValue
  winner?: OptaWinner
  matchLengthMin?: number
  matchLengthSec?: number
  period?: OptaMatchPeriod[]
  scores?: OptaMatchScores
}

export type OptaMatchLiveData = {
  matchDetails: OptaMatchDetails
}
export type OptaMatchItem = {
  matchInfo: OptaMatchInfo
  liveData: OptaMatchLiveData
}

export type RequestMatchesParams = {
  matchTimeRangeStart: string
  matchTimeRangeEnd: string
  live?: boolean
  tournamentCalendarId?: string
  contestantId?: string
}

export type OptaMatchList = OptaMatchItem[]

export enum TeamStandingsType {
  Total = 'total',
  Home = 'home',
  Away = 'away',
  FormTotal = 'form-total',
  FormHome = 'form-home',
  FormAway = 'form-away',
  HalfTimeTotal = 'half-time-total',
  HalfTimeHome = 'half-time-home',
  HalfTimeAway = 'half-time-away',
  Attendance = 'attendance',
  OverUnder = 'over-under',
}

export type OptaTeamStandingsRanking = {
  rank: number
  rankStatus?: string
  lastRank?: number
  lastSix?: string
  contestantId: OptaUUID
  contestantName: string
  contestantShortName: string
  contestantClubName: string
  contestantCode: string
  points: number
  matchesPlayed: number
  matchesWon: number
  matchesLost: number
  matchesDrawn: number
  goalsFor: number
  goalsAgainst: number
  goaldifference: string
}

export type OptaTeamStanding = Pick<
  OptaTeamStandingsRanking,
  | 'points'
  | 'goaldifference'
  | 'goalsAgainst'
  | 'goalsFor'
  | 'matchesLost'
  | 'matchesDrawn'
  | 'matchesWon'
  | 'matchesPlayed'
  | 'rank'
  | 'contestantId'
  | 'contestantName'
>

export type OptaTeamStandingsGroup = {
  type: `${TeamStandingsType}`
  groupId?: OptaUUID
  groupName?: string
  ranking: OptaTeamStandingsRanking[]
}

export type OptaDivision = OptaTeamStandingsGroup[]
export type OptaDivisionWithHistory = {
  pageId: Nullable<string>
  division: OptaDivision
  prevDivision: Nullable<OptaDivisionWithHistory>
}

export type OptaTeamStandingsStage = {
  division: OptaDivision
  tournamentCalendar: OptaTournamentCalendar
}

export type OptaMatchItemTeam = {
  id: OptaUUID
  name: string
}

export type MatchItem = {
  id: OptaUUID
  startsAt: string
  hasStarted: boolean
  time?: {
    min: number
    sec: number
  }
  home: OptaMatchItemTeam
  away: OptaMatchItemTeam
  score?: {
    home: number
    away: number
  }
  matchStatus: OptaMatchStatus
}

export enum StandingsTypes {
  GROUPS = 'GROUPS',
  LEAGUE = 'LEAGUE',
}

export type MatchCommentaryData = {
  matchInfo: OptaMatchInfo
  messages: CommentaryMessages[]
}

export type CommentaryMessages = {
  language?: string
  message: CommentaryMessage[]
}

export type CommentaryData = {
  matchId: string
  startsAt: string
  teamsIds: {
    home?: string
    away?: string
  }
  message: CommentaryMessage[]
}

export type CommentaryMessage = {
  comment: string
  id: string
  lastModified: string
  minute: string
  period: string
  second: string
  time: string
  timestamp: string
  type: string
  playerRef1: string
  playerRef2: string
  teamRef1: string
  teamRef2: string
}
